<template>
	<transition name="fade">
		<div v-if="isOpen" class="fixed inset-0 z-50 overflow-y-auto" @click.self="$emit('close')">
			<div class="flex sm:items-center sm:justify-center min-h-screen sm:px-4 sm:pt-4 sm:pb-20 text-center">
				<div class="fixed inset-0 transition-opacity bg-black bg-opacity-50 dark:bg-opacity-50" @click="$emit('close')"></div>
				<div class="inline-block align-bottom sm:align-middle bg-white dark:bg-gray-800 sm:rounded-lg text-left overflow-hidden shadow-xl transform transition-all min-h-screen w-full fixed inset-x-0 top-0 sm:static sm:min-h-0 sm:max-w-3xl sm:w-full flex flex-col">
					<div class="bg-gradient-to-tr from-primary to-secondary dark:from-primary-light/10 dark:to-secondary-light/10 px-4 py-4 sm:px-6 flex-shrink-0">
						<h3 class="text-lg leading-6 font-medium text-white">Update Viewers</h3>
					</div>
					<div class="bg-white dark:bg-gray-800 px-3 pt-4 pb-3 sm:p-6 sm:pb-4 overflow-y-auto flex-1">
						<div class="space-y-5">
							<div>
								<label class="text-sm font-medium text-slate-700 dark:text-slate-200">
								Viewers (By Position)
								</label>
								<div class="relative mt-1" ref="positionContainer">
									<input
										v-model="positionSearch"
										type="text"
										placeholder="Search positions..."
										@focus="positionOpen = true"
										class="w-full rounded-xl border border-slate-300 dark:border-slate-600 px-3 py-2 bg-white dark:bg-slate-900 text-sm focus:ring-2 focus:ring-primary/30 focus:border-primary outline-none transition"
									/>
									<div
										v-if="positionOpen"
										class="absolute z-20 mt-1 w-full rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 shadow-lg max-h-48 overflow-y-auto"
									>
										<label
										v-for="pos in filteredPositions"
										:key="pos.id"
										class="flex items-center gap-2 px-3 py-2 hover:bg-slate-50 dark:hover:bg-slate-800 cursor-pointer text-sm transition-colors"
										>
										<input type="checkbox" :value="pos.id" v-model="selectedPositionIds" class="rounded border-slate-300 text-primary focus:ring-primary" />
										<span>{{ pos.name }}</span>
										</label>
										<p v-if="!filteredPositions.length" class="px-3 py-2 text-sm text-slate-400 italic">No positions match</p>
									</div>
								</div>
								<div v-if="fetching" class="flex items-center gap-2 mt-2 text-sm text-slate-400">
									<Loader2 class="w-4 h-4 animate-spin" />
									Loading...
								</div>
								<div v-else-if="selectedPositionIds.length" class="flex flex-wrap gap-1.5 mt-2">
									<span
										v-for="id in selectedPositionIds" :key="'psel-'+id"
										class="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-primary/10 dark:bg-primary-dark/10 text-primary dark:text-primary-dark"
									>
										{{ getPositionName(id) }}
										<button type="button" @click="removePosition(id)" class="mr-auto hover:text-primary/70 dark:hover:text-primary-dark/70 leading-none">
											<X class="w-3.5 h-3.5" />
										</button>
									</span>
								</div>
								<p v-else class="mt-1 text-xs text-slate-400 italic">None selected</p>
							</div>

							<div>
								<label class="text-sm font-medium text-slate-700 dark:text-slate-200">
								Viewers (By Specific Employee)
								</label>
								<div class="relative mt-1" ref="employeeContainer">
									<input
										v-model="employeeSearch"
										type="text"
										placeholder="Search employees..."
										@focus="employeeOpen = true"
										class="w-full rounded-xl border border-slate-300 dark:border-slate-600 px-3 py-2 bg-white dark:bg-slate-900 text-sm focus:ring-2 focus:ring-primary/30 focus:border-primary outline-none transition"
									/>
									<div
										v-if="employeeOpen"
										class="absolute z-20 mt-1 w-full rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 shadow-lg max-h-48 overflow-y-auto"
									>
										<label
										v-for="emp in filteredEmployees"
										:key="emp.id"
										class="flex items-center gap-2 px-3 py-2 hover:bg-slate-50 dark:hover:bg-slate-800 cursor-pointer text-sm transition-colors"
										>
										<input type="checkbox" :value="emp.id" v-model="selectedEmployeeIds" class="rounded border-slate-300 text-primary focus:ring-primary" />
										<span>{{ emp.first_name ? emp.first_name + ' ' + emp.last_name : emp.username }}{{ emp.position ? ' (' + emp.position + ')' : '' }}</span>
										</label>
										<p v-if="!filteredEmployees.length" class="px-3 py-2 text-sm text-slate-400 italic">No employees match</p>
									</div>
								</div>
								<div v-if="fetching" class="flex items-center gap-2 mt-2 text-sm text-slate-400">
									<Loader2 class="w-4 h-4 animate-spin" />
									Loading...
								</div>
								<div v-else-if="selectedEmployeeIds.length" class="flex flex-wrap gap-1.5 mt-2">
									<span
										v-for="id in selectedEmployeeIds" :key="'esel-'+id"
										class="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-primary/10 dark:bg-primary-dark/10 text-primary dark:text-primary-dark"
									>
										{{ getEmployeeName(id) }}
										<button type="button" @click="removeEmployee(id)" class="hover:text-primary/70 dark:hover:text-primary-dark/70 leading-none">
											<X class="w-3.5 h-3.5" />
										</button>
									</span>
								</div>
								<p v-else class="mt-1 text-xs text-slate-400 italic">None selected</p>
							</div>
						</div>
					</div>

					<div class="bg-gray-50 dark:bg-gray-700 px-4 py-3 flex flex-col gap-2 sm:px-6 sm:flex-row-reverse sm:gap-0">
						<button type="button"
							class="w-full sm:w-auto px-4 py-2 rounded-xl font-semibold text-sm text-white bg-primary hover:bg-secondary shadow-sm hover:shadow-md transition-all flex items-center justify-center focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#ff8e48]/60 disabled:opacity-50 sm:ml-3"
							@click="handleSave" :disabled="loading">
							<Loader2 v-if="loading" class="w-4 h-4 mr-2 animate-spin" />
							<span v-else>Update Viewers</span>
						</button>
						<button type="button"
							class="w-full sm:w-auto px-4 py-2 rounded-xl font-semibold text-sm bg-slate-50 dark:bg-slate-700/30 text-slate-700 dark:text-slate-200 border border-slate-200/60 dark:border-slate-700/60 hover:bg-slate-100 dark:hover:bg-slate-700/50 transition-all disabled:opacity-50"
							@click="$emit('close')" :disabled="loading">
							Cancel
						</button>
					</div>
				</div>
			</div>
		</div>
	</transition>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted, onUnmounted } from 'vue'
import { positionService, type Position } from '@/services/positionService'
import { employeeService, type Employee } from '@/services/employeeService'
import { Loader2, X } from 'lucide-vue-next'

const props = defineProps<{
  isOpen: boolean
  materialId: number
  positionViewerIds: number[]
  userViewerIds: number[]
  loading?: boolean
}>()

const emit = defineEmits<{
  (e: 'close'): void
  (e: 'save', data: { materialId: number; positionViewerIds: number[]; userViewerIds: number[] }): void
}>()

const positions = ref<Position[]>([])
const employees = ref<Employee[]>([])
const selectedPositionIds = ref<number[]>([])
const selectedEmployeeIds = ref<number[]>([])
const fetching = ref(false)

const positionSearch = ref('')
const employeeSearch = ref('')

const positionOpen = ref(false)
const employeeOpen = ref(false)
const positionContainer = ref<HTMLElement | null>(null)
const employeeContainer = ref<HTMLElement | null>(null)

const filteredPositions = computed(() => {
  if (!positionSearch.value) return positions.value
  const q = positionSearch.value.toLowerCase()
  return positions.value.filter(p => p.name.toLowerCase().includes(q))
})

const filteredEmployees = computed(() => {
  if (!employeeSearch.value) return employees.value
  const q = employeeSearch.value.toLowerCase()
  return employees.value.filter(e => {
    const name = (e.first_name ? e.first_name + ' ' + e.last_name : e.username || '').toLowerCase()
    const pos = (e.position || '').toLowerCase()
    return name.includes(q) || pos.includes(q)
  })
})

const getPositionName = (id: number) => positions.value.find(p => p.id === id)?.name || 'Unknown'

const getEmployeeName = (id: number) => {
  const emp = employees.value.find(e => e.id === id)
  if (!emp) return 'Unknown'
  const name = emp.first_name ? emp.first_name + ' ' + emp.last_name : emp.username || 'Unknown'
  return emp.position ? `${name} (${emp.position})` : name
}

const removePosition = (id: number) => {
  selectedPositionIds.value = selectedPositionIds.value.filter(v => v !== id)
}

const removeEmployee = (id: number) => {
  selectedEmployeeIds.value = selectedEmployeeIds.value.filter(v => v !== id)
}

const handleClickOutside = (event: MouseEvent) => {
  const target = event.target as HTMLElement
  if (positionContainer.value && !positionContainer.value.contains(target)) {
    positionOpen.value = false
  }
  if (employeeContainer.value && !employeeContainer.value.contains(target)) {
    employeeOpen.value = false
  }
}

onMounted(() => {
  document.addEventListener('mousedown', handleClickOutside)
})

onUnmounted(() => {
  document.removeEventListener('mousedown', handleClickOutside)
})

let fetched = false
const fetchDependencies = async () => {
  if (fetched) return
  fetching.value = true
  try {
    const [posRes, empRes] = await Promise.all([
      positionService.getPositions(),
      employeeService.getEmployees({ per_page: 1000 })
    ])
    positions.value = posRes
    employees.value = empRes.data || []
    fetched = true
  } catch (err) {
    console.error('Failed to fetch viewers data:', err)
  } finally {
    fetching.value = false
  }
}

watch(() => props.isOpen, async (open) => {
  if (open) {
    await fetchDependencies()
    selectedPositionIds.value = [...props.positionViewerIds]
    selectedEmployeeIds.value = [...props.userViewerIds]
    positionSearch.value = ''
    employeeSearch.value = ''
    positionOpen.value = false
    employeeOpen.value = false
  }
}, { immediate: true })

const handleSave = () => {
  emit('save', {
    materialId: props.materialId,
    positionViewerIds: selectedPositionIds.value,
    userViewerIds: selectedEmployeeIds.value,
  })
}
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
